import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import lang from '../../../lib/language';
import { ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  REQUEST_CALL_NAME_CONFIRM_EMAIL,
  REQUEST_CALL_NAME_GET_MANUAL_TOKEN,
} from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import { login as serviceLogin } from '../../../lib/store/common-actions';
import { setInitiallySelectedPlanId as setInitiallySelectedPlanIdCall } from '../../../lib/store/crm';
import { parseOnboardingErrorCode } from '../../../lib/store/crm/helpers';
import { NullableNumber, NullableString } from '../../../lib/util/types';

import EmailConfirmedModal from '../../../components/Enroll/EmailConfirmedModal/EmailConfirmedModal';
import ExpiredVerificationModal from '../../../components/Enroll/ExpiredVerificationModal/ExpiredVerificationModal';
import VerifyEmailModal from '../../../components/Enroll/VerifyEmailModal/VerifyEmailModal';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollVerifyEmail.module.scss';

type Props = ReduxProps;

const EnrollVerifyEmail = (props: Props): JSX.Element => {
  const {
    isAppLoaded,
    accessToken,
    responseStatus,
    responseStatusCode,
    requestCallName,
    tradingPlatformId,
    authenticate,
    setInitiallySelectedPlanId,
    initiallySelectedPlanId,
  } = props;

  const location = useLocation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const { form } = EnrollmentState.use();
  const [isVerifyLinkExpired, setIsVerifyLinkExpired] = useState<boolean>(false);
  const [verifyEmailModalOpened, setVerifyEmailModalOpened] = useState<boolean>(true);
  const [emailConfirmedModal, setEmailConfirmedModal] = useState<boolean>(false);
  const isConfirmURL = location.search?.match(/token[=]/);

  const checkAndRedirectToPreviousScreen = (): void => {
    if (!form.steps.isAuthInfoPassed) {
      history.replace(UI_ROUTES.secondEnroll);
    }
  };

  useEffect(() => {
    if (!isAppLoaded) {
      return;
    }

    if (isConfirmURL) {
      const queryParams = new URLSearchParams(location.search);

      if (queryParams) {
        const tradingPlanId = Number(queryParams.get('TradingPlanId'));
        if (tradingPlanId && tradingPlanId !== initiallySelectedPlanId) {
          setInitiallySelectedPlanId(tradingPlanId);
        }

        authenticate(
          queryParams.get('Email'),
          queryParams.get('token'),
          queryParams.get('FirstName'),
          queryParams.get('LastName'),
          queryParams.get('CountryOfResidence'),
        );
      } else {
        checkAndRedirectToPreviousScreen();
      }
    } else {
      checkAndRedirectToPreviousScreen();
    }
  }, [isAppLoaded, authenticate, location.search, form.steps.isAuthInfoPassed]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (accessToken && tradingPlatformId) {
      setEmailConfirmedModal(true);
    }
  }, [accessToken, tradingPlatformId]);

  useEffect(() => {
    if (
      responseStatus
      && responseStatus === ENROLL_REQUEST_STATE.ERROR
      && parseOnboardingErrorCode(responseStatusCode!) === ENROLL_REQUEST_STATE.REQUEST_ERROR
      && (requestCallName === REQUEST_CALL_NAME_CONFIRM_EMAIL || requestCallName === REQUEST_CALL_NAME_GET_MANUAL_TOKEN)
    ) {
      setIsVerifyLinkExpired(true);
    }
  }, [requestCallName, responseStatus, responseStatusCode]);

  const isEmailVerifying = verifyEmailModalOpened && !emailConfirmedModal;

  return (
    <Layout
      transparentContent
      formTitle={isVerifyLinkExpired ? lang.commonOnboardingExpiredVerificationLinkTitle() : ''}
      contentStyle={styles.verificationContent}
      displayBackButton={!isMobile}
      displayFooter={false}
      displayNextButton={!isEmailVerifying}
      nextButtonText={lang.commonContinueButton2()}
      displayLoginButton={false}
      onSubmit={() => {
        EnrollmentState.setVerifyEmailStepPassed(true);
        history.push(UI_ROUTES.enrollPhoneVerificaion);
      }}
    >
      <VerifyEmailModal
        largeText
        noModalDisplay
        userEmail={form.email}
        handleCloseVerifyEmailModal={() => setVerifyEmailModalOpened(false)}
        forceNoRender={isConfirmURL || isVerifyLinkExpired || !isEmailVerifying}
      />
      <EmailConfirmedModal
        isOpened={!isVerifyLinkExpired && !isEmailVerifying}
        noModalDisplay
        forceNoRender={isVerifyLinkExpired || isEmailVerifying}
      />
      <ExpiredVerificationModal noModalDisplay forceNoRender={!isVerifyLinkExpired} />
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  login: (): void => {
    dispatch(serviceLogin('login'));
  },
  setInitiallySelectedPlanId: (planId: NullableNumber) => dispatch(setInitiallySelectedPlanIdCall(planId)),
  authenticate: (
    email: NullableString,
    token: NullableString,
    firstName: NullableString,
    lastName: NullableString,
    country: NullableString,
  ): void => {
    dispatch(enrollAuth({
      requestType: ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL, email, token, firstName, lastName, country,
    }));
  },
});

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  accessToken: state.auth.enroll.accessToken,
  responseStatus: state.auth.enroll?.status,
  responseStatusCode: state.auth.enroll?.statusCode,
  requestCallName: state.auth.enroll?.requestCallName,
  tradingPlatformId: state.crm.tradingPlatformId,
  initiallySelectedPlanId: state.crm.enroll.initiallySelectedPlanId,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollVerifyEmail);
